import { useEffect, useMemo, useState } from 'react'
import { createFileRoute, getRouteApi } from '@tanstack/react-router'
import {
  useDownloadReportsDashboardPdf,
  useHasFeature,
} from '@workspace/react-query'
import { Button } from '@workspace/ui/components/button'
import { Input } from '@workspace/ui/components/input'
import type { ReportsDashboardFilters } from '@workspace/utils/types'
import { Download, Loader2, Search } from 'lucide-react'
import { toast } from 'sonner'
import { authClient } from '@/lib/auth'
import { requireActiveSubscription, requireRoles } from '@/lib/middleware'
import { useTopNav } from '@/context/top-nav-provider'
import { Main } from '@/components/layout/main'
import { ClientReportBuilder } from '@/features/reports/builder/client-report-builder'
import {
  buildReportsDashboardFilters,
  reportsSearchSchema,
} from '@/features/reports/dashboard/global-filter-bar'
import { ReportLibrary } from '@/features/reports/dashboard/report-library'
import { ReportsDashboard } from '@/features/reports/dashboard/reports-dashboard'
import { AutoDownloadExport } from '@/features/reports/auto-download-export'

const route = getRouteApi('/_authenticated/_organization/reports/')

/**
 * Header "Export" — one click downloads a branded PDF of the Reports Dashboard
 * in the report-pack house style: org-level KPIs, trends, charge status, value
 * by service type, needs attention, operational insights, utilisation
 * distribution, participant insights, the AI business summary and any pinned
 * saved insights — respecting the current filters. The server renders it
 * synchronously and streams it straight back, so the file downloads directly
 * (no queue, no dialog). Per-participant detail stays under "Build report".
 */
function ExportButton({
  organizationId,
  filters,
}: {
  organizationId: string
  filters: ReportsDashboardFilters
}) {
  const download = useDownloadReportsDashboardPdf({
    organizationId,
    // M15 async path: the signed URL arrives via the job poll, outside any
    // click gesture — surface a clickable action instead of auto-opening.
    // Persistent (never auto-dismissing) so the download link can't vanish.
    onAsyncReady: (result) => {
      toast.success('Report PDF ready', {
        duration: Infinity,
        closeButton: true,
        action: {
          label: 'Download',
          onClick: () => window.open(result.downloadUrl, '_blank'),
        },
      })
    },
    onAsyncError: (error) => {
      toast.error(error.message)
    },
  })

  const onExport = async () => {
    try {
      const result = await download.mutateAsync({ filters })
      if (result.jobId) {
        toast.info('Exporting PDF — a download link will appear when ready')
        return
      }
      const { blob, filename } = result
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename || 'Reports-Dashboard.pdf'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to export PDF'
      )
    }
  }

  return (
    <Button
      onClick={onExport}
      disabled={download.isGenerating}
      className='bg-[#11302c] text-white hover:bg-[#0c2420]'
    >
      {download.isGenerating ? (
        <Loader2 className='mr-2 h-4 w-4 animate-spin' />
      ) : (
        <Download className='mr-2 h-4 w-4' />
      )}
      {download.isGenerating ? 'Exporting…' : 'Export'}
    </Button>
  )
}

function Reports() {
  const { data: activeOrganization, isPending: isLoadingOrganization } =
    authClient.useActiveOrganization()
  const { setLinks, setFixed } = useTopNav()
  const search = route.useSearch()
  const setSearch = route.useNavigate()
  const [librarySearch, setLibrarySearch] = useState('')
  const hasDashboard = useHasFeature(
    activeOrganization?.id ?? null,
    'reports.dashboard'
  )
  const filters = useMemo(() => buildReportsDashboardFilters(search), [search])

  useEffect(() => {
    setLinks([
      { title: 'Overview', href: '/' },
      { title: 'Reports', href: '/reports', isActive: true },
    ])
    setFixed(true)
    return () => {
      setLinks([])
    }
  }, [setLinks, setFixed])

  if (isLoadingOrganization) {
    return (
      <Main className='flex flex-1 flex-col gap-6'>
        <div className='flex animate-pulse flex-col gap-2'>
          <div className='bg-muted h-8 w-64 rounded' />
          <div className='bg-muted h-4 w-96 rounded' />
        </div>
      </Main>
    )
  }

  if (!activeOrganization) {
    return null
  }

  const organizationId = activeOrganization.id

  return (
    <Main className='flex flex-1 flex-col gap-6'>
      {/* Arrived from a scheduled-report email link — fetch the file straight
          away instead of making the recipient hunt for it. */}
      {search.export ? (
        <AutoDownloadExport
          organizationId={organizationId}
          exportId={search.export}
          onHandled={() =>
            setSearch({
              search: (prev) => ({ ...prev, export: undefined }),
              replace: true,
            })
          }
        />
      ) : null}

      {/* Page header — eyebrow, title, subtitle + search / build report / export */}
      <div className='flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between'>
        <div>
          <p className='text-primary text-xs font-semibold tracking-wider uppercase'>
            Reports
          </p>
          <h1 className='mt-1 text-2xl font-bold tracking-tight'>
            Reports Dashboard
          </h1>
          <p className='text-muted-foreground mt-1 text-sm'>
            Billable activity, utilisation, workforce and compliance
            {activeOrganization.name ? ` · ${activeOrganization.name}` : ''}
          </p>
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          <div className='relative'>
            <Search className='text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2' />
            <Input
              value={librarySearch}
              onChange={(e) => setLibrarySearch(e.target.value)}
              placeholder='Search reports…'
              className='w-full pl-8 sm:w-56'
            />
          </div>
          {hasDashboard && (
            <>
              <ClientReportBuilder
                organizationId={organizationId}
                filters={filters}
                autoOpen={
                  search.build || search.templateId
                    ? {
                        section: search.build,
                        participantId: search.participantId,
                        templateId: search.templateId,
                      }
                    : undefined
                }
              />
              <ExportButton organizationId={organizationId} filters={filters} />
            </>
          )}
        </div>
      </div>

      {hasDashboard ? (
        <>
          <ReportsDashboard
            organizationId={organizationId}
            filters={filters}
            search={search}
            highlightInsight={search.highlightInsight}
            onSearchChange={(updater) =>
              // Filter tweaks replace the current history entry (mirror staff
              // search) so Back leaves /reports instead of stepping through
              // every filter change (plan Task A7).
              setSearch({ search: updater, replace: true })
            }
          />
          <ReportLibrary
            search={librarySearch}
            filters={filters}
            organizationId={organizationId}
          />
        </>
      ) : (
        <ReportLibrary search={librarySearch} filters={filters} />
      )}
    </Main>
  )
}

export const Route = createFileRoute('/_authenticated/_organization/reports/')({
  validateSearch: reportsSearchSchema,
  beforeLoad: async () => {
    // Reports Dashboard is owner/admin only for now (matches the API/data-layer
    // gate in resolve-scope.action.ts REPORTS_DASHBOARD_ALLOWED_ROLES — PRD v1.3
    // §18). Widening this is a deliberate product decision.
    await requireRoles(['owner', 'admin'])
    await requireActiveSubscription()
  },
  component: Reports,
})
